import { supabase } from "@/integrations/supabase/client";
import { logger, serializeError } from "@/lib/logger";

export interface GeographyLocation {
  city: string;
  country: string;
  uniqueParticipants: number;
  coordinates: [number, number] | null;
}

export interface GeographyStats {
  locations: GeographyLocation[];
  totalUniqueFinishers: number;
  totalCities: number;
  totalCountries: number;
}

/**
 * Fetch geography statistics with unique finishers per location.
 *
 * Uses two SECURITY DEFINER RPCs so authenticated callers (hosts,
 * participants) see the same global numbers as anonymous visitors:
 *   - get_geography_locations(): per-location aggregates
 *   - get_hero_stats():          totals (finishers, cities, countries)
 */
export async function fetchGeographyStats(): Promise<GeographyStats> {
  const heroStats = await fetchHeroStats();

  // Try the SECURITY DEFINER RPC first (returns global numbers regardless
  // of caller role). Fall back to the direct table query if the function
  // isn't deployed yet — keeps the page working between code deploy and
  // migration apply. The fallback is subject to RLS so authenticated
  // users may see fewer locations until the migration runs.
  const rpcResult = await (
    supabase.rpc as unknown as (
      fn: string,
    ) => Promise<{
      data: unknown;
      error: { code?: string; message: string } | null;
    }>
  )("get_geography_locations");

  if (!rpcResult.error) {
    const rows =
      (rpcResult.data as Array<{
        city: string;
        country: string;
        unique_participants: number;
        latitude: number | null;
        longitude: number | null;
      }> | null) ?? [];

    const locations: GeographyLocation[] = rows.map((r) => ({
      city: r.city,
      country: r.country ?? "Unknown",
      uniqueParticipants: Number(r.unique_participants ?? 0),
      coordinates:
        r.latitude != null && r.longitude != null
          ? ([r.longitude, r.latitude] as [number, number])
          : null,
    }));

    return {
      locations,
      totalUniqueFinishers: heroStats.uniqueFinishers,
      totalCities: heroStats.totalCities,
      totalCountries: heroStats.totalCountries,
    };
  }

  // Fallback: RPC missing (404 / PGRST202). Use the original client-side
  // aggregation. Logs once so the missing migration is visible in the
  // console but the page still renders.
  if (rpcResult.error.code === "PGRST202") {
    logger.warn(
      "get_geography_locations RPC not deployed yet, falling back to direct query.",
    );
  } else {
    logger.error("Error fetching geography data via RPC", serializeError(rpcResult.error));
  }

  const { data, error } = await supabase.from("participation_member").select(`
    person_id,
    participation:participation_id!inner (
      location:location_id (
        city,
        country,
        latitude,
        longitude
      ),
      result!inner (
        id
      )
    )
  `);

  if (error) {
    throw error;
  }

  const locationMap = new Map<
    string,
    {
      city: string;
      country: string;
      personIds: Set<number>;
      latitude: number | null;
      longitude: number | null;
    }
  >();
  data?.forEach((row) => {
    const participation = row.participation as {
      location: {
        city: string;
        country: string | null;
        latitude: number | null;
        longitude: number | null;
      };
    } | null;
    if (!participation?.location) return;

    const { city, country, latitude, longitude } = participation.location;
    if (!city) return;

    const key = `${city}|${country || "Unknown"}`;
    if (!locationMap.has(key)) {
      locationMap.set(key, {
        city,
        country: country || "Unknown",
        personIds: new Set(),
        latitude,
        longitude,
      });
    }
    locationMap.get(key)!.personIds.add(row.person_id);
  });

  const locations: GeographyLocation[] = Array.from(locationMap.values())
    .map((loc) => ({
      city: loc.city,
      country: loc.country,
      uniqueParticipants: loc.personIds.size,
      coordinates:
        loc.latitude != null && loc.longitude != null
          ? ([loc.longitude, loc.latitude] as [number, number])
          : null,
    }))
    .sort((a, b) => b.uniqueParticipants - a.uniqueParticipants);

  return {
    locations,
    totalUniqueFinishers: heroStats.uniqueFinishers,
    totalCities: heroStats.totalCities,
    totalCountries: heroStats.totalCountries,
  };
}

export interface HeroStatsData {
  totalFinishers: number;
  uniqueFinishers: number;
  finishedEvents: number;
  totalCities: number;
  totalCountries: number;
}

/**
 * Fetch hero stats for main page via a single Supabase RPC call.
 * The `get_hero_stats` SQL function computes all four numbers server-side.
 */
export async function fetchHeroStats(): Promise<HeroStatsData> {
  const { data, error } = await supabase.rpc("get_hero_stats");

  if (error) throw error;

  const stats = data as {
    total_finishers: number;
    unique_finishers: number;
    finished_events: number;
    total_cities: number;
    total_countries: number;
  };

  return {
    totalFinishers: stats.total_finishers,
    uniqueFinishers: stats.unique_finishers,
    finishedEvents: stats.finished_events,
    totalCities: stats.total_cities,
    totalCountries: stats.total_countries,
  };
}
